/**
 * Durable auth-lane telemetry.
 *
 * Supabase's analytics `auth_logs` retention on this project is only minutes
 * long, so an overnight session death leaves no recoverable evidence. This
 * module writes one row per auth-lifecycle event into `public.auth_event_logs`,
 * which is insert-only for the owning user and purged after 30 days by cron.
 *
 * Every call is fire-and-forget and must NEVER throw or block an auth path.
 */
import { supabase } from '@/integrations/supabase/client';
import { getStableDeviceId } from '@/lib/auth/deviceId';
import { isNativePlatform } from '@/lib/platform';

export type AuthEventName =
  | 'cold_boot'
  | 'app_pause'
  | 'app_resume'
  | 'resume_catchup'
  | 'hot_resume_recovery'
  | 'token_refreshed'
  | 'signed_out_intercepted'
  | 'explicit_signout'
  | 'force_signout';

let cachedDeviceId: string | null = null;
const resolveDeviceId = async (): Promise<string | null> => {
  if (cachedDeviceId) return cachedDeviceId;
  try {
    cachedDeviceId = await getStableDeviceId();
  } catch {
    cachedDeviceId = null;
  }
  return cachedDeviceId;
};

const platformLabel = (): string => {
  try {
    if (isNativePlatform()) {
      const cap = (window as unknown as { Capacitor?: { platform?: string } }).Capacitor;
      return `native:${cap?.platform ?? 'unknown'}`;
    }
  } catch { /* ignore */ }
  return 'web';
};

/**
 * Records an auth event. `userId` is required by RLS (`auth.uid() = user_id`);
 * when omitted we resolve it from the live session, and skip the write if
 * there is no signed-in user (an anonymous insert would be rejected anyway).
 */
export const logAuthEvent = (
  event: AuthEventName,
  detail: Record<string, unknown> = {},
  userId?: string | null,
): void => {
  void (async () => {
    try {
      let uid = userId ?? null;
      if (!uid) {
        const { data } = await supabase.auth.getSession();
        uid = data.session?.user?.id ?? null;
      }
      if (!uid) return;

      const deviceId = await resolveDeviceId();
      const { error } = await supabase.from('auth_event_logs').insert({
        user_id: uid,
        device_id: deviceId,
        platform: platformLabel(),
        event,
        detail: detail as never,
        client_at: new Date().toISOString(),
      });
      if (error) {
        console.warn('[auth-event-log] insert failed (non-fatal):', error.message);
      }
    } catch (e) {
      console.warn('[auth-event-log] failed (non-fatal):', e);
    }
  })();
};

/* ------------------------------------------------------------------ *
 * Backgrounded-duration tracking
 *
 * The overnight bug hinges on how long the app was suspended. We stamp
 * the moment we go hidden/paused and read it back on resume so a single
 * `app_resume` row carries `backgrounded_ms` (~8h for an overnight sleep).
 * ------------------------------------------------------------------ */

const BACKGROUNDED_AT_KEY = 'lr_auth_backgrounded_at_v1';

export const markBackgrounded = (): void => {
  try { localStorage.setItem(BACKGROUNDED_AT_KEY, String(Date.now())); } catch { /* ignore */ }
};

export const consumeBackgroundedMs = (): number | null => {
  try {
    const raw = localStorage.getItem(BACKGROUNDED_AT_KEY);
    if (!raw) return null;
    localStorage.removeItem(BACKGROUNDED_AT_KEY);
    const at = Number(raw);
    if (!Number.isFinite(at)) return null;
    return Math.max(0, Date.now() - at);
  } catch {
    return null;
  }
};
